import React from 'react';
import { BsTelephone } from 'react-icons/bs';
import moment from 'moment';
import { Oval } from 'react-loader-spinner';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { getDailyProgressV2, getPunchCardsForDriverV2, getDriverLocationV2 } from '../../../DriversV2/driversV2Slice';
import { DailyProgressItem } from '../../../../models';
import { PhoneNumber } from '../../../../components';

import './DailyProgressTab.scss';

import { PunchCardCard } from '../PunchCardCard/PunchCardCard';
import { DriverLocationMap } from './DriverLocationMap';

export interface DailyProgressTabProps {
  userId: string;
  // drivers-table PK (DriverV2.id); required for the driver-location fetch,
  // which is keyed on drivers.id (userId 404s there).
  driverId: string;
}

const typeLabels: Record<string, string> = {
  delivery: 'Delivery',
  pick_up: 'Pickup',
  dump_yard: 'Dump Yard',
};

const typeColors: Record<string, string> = {
  delivery: '#9DBFE4',
  pick_up: '#FFDDAA',
  dump_yard: '#AED8D1',
};

export function DailyProgressTab(props: DailyProgressTabProps) {
  const { userId, driverId } = props;
  const dispatch = useAppDispatch();

  const dailyProgress = useAppSelector(state => state.driversV2.dailyProgress);
  const getDailyProgressAPI = useAppSelector(state => state.driversV2.getDailyProgress);
  const driverLocation = useAppSelector(state => state.driversV2.driverLocation);
  const driverName = useAppSelector(
    state => state.driversV2.driverLocationName ?? state.driversV2.selectedDriver?.name
  );

  const items = dailyProgress?.items || [];
  const scheduledItems = items.filter(i => i.status === 'scheduled');
  const nextStop = scheduledItems[0];
  const upcomingStops = scheduledItems.slice(1);

  const deliveryCount = items.filter(i => i.type === 'delivery').length;
  const pickupCount = items.filter(i => i.type === 'pick_up').length;
  const dumpYardCount = items.filter(i => i.type === 'dump_yard').length;
  const totalCount = deliveryCount + pickupCount + dumpYardCount;

  const remainingStats = dailyProgress?.remainingStats;
  const noSchedule = !dailyProgress?.scheduleId || items.length === 0;
  const isCompleted = items.length > 0 && scheduledItems.length === 0;

  const formatDistance = (meters: number): string => {
    const miles = meters / 1609.34;
    if (miles < 0.1) return 'Less than 0.1 mi';
    return `${miles.toFixed(1)} mi`;
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 1) return 'Less than 1 min';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} hr`;
    return `${hours} hr ${mins} min`;
  };

  React.useEffect(() => {
    const today = moment().format('YYYY-MM-DD');
    dispatch(getDailyProgressV2({
      date: today,
      userId
    }));
    dispatch(getPunchCardsForDriverV2({
      driverId: userId,
      fromDate: today,
      toDate: today
    }));
    dispatch(getDriverLocationV2({ driverId }));
  }, [userId, driverId]);

  const getTypeColor = (type: string) => typeColors[type] || '#AED8D1';

  const renderNextStop = () => {
    if (!nextStop) return null;

    return (
      <div className='next-stop-card'>
        <div className='next-stop-header'>
          <span className='next-stop-title'>Next Stop</span>
          <div className='pills'>
            <span className='pill' style={{ backgroundColor: getTypeColor(nextStop.type) }}>
              {typeLabels[nextStop.type] || nextStop.type}
            </span>
            {nextStop.dumpsterSize && (
              <span className='pill'>{nextStop.dumpsterSize}</span>
            )}
          </div>
        </div>

        {nextStop.price && (
          <div className='price'>Price: ${nextStop.price}</div>
        )}

        <div className='address'>{nextStop.address}, {nextStop.zip}</div>

        {nextStop.contactName && (
          <div className='contact-name'>{nextStop.contactName}</div>
        )}

        {nextStop.contactPhone && (
          <div className='contact-phone'>
            <BsTelephone className='phone-icon' />
            <PhoneNumber phoneNumber={nextStop.contactPhone} />
          </div>
        )}

        {nextStop.specialInstructions && (
          <div className='special-instructions'>
            <div className='label'>Special Instructions</div>
            <div className='text'>{nextStop.specialInstructions}</div>
          </div>
        )}
      </div>
    );
  };

  const renderCompletedMessage = () => {
    if (!isCompleted) return null;

    return (
      <div className='completed-message'>
        Schedule is completed.
      </div>
    );
  };

  const renderStatusCard = () => {
    if (noSchedule) return null;

    return (
      <div className='status-card'>
        <div className='status-header'>
          <span className='status-title'>Total: {totalCount}</span>
        </div>
        <div className='status-divider' />
        <div className='status-content'>
          <div className='status-tile'>
            <span className='tile-label'>Deliveries</span>
            <span className='tile-count'>{deliveryCount}</span>
          </div>
          <div className='status-vertical-divider' />
          <div className='status-tile'>
            <span className='tile-label'>Pick ups</span>
            <span className='tile-count'>{pickupCount}</span>
          </div>
          <div className='status-vertical-divider' />
          <div className='status-tile'>
            <span className='tile-label'>Dump yards</span>
            <span className='tile-count'>{dumpYardCount}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderRemainingStats = () => {
    if (noSchedule || !remainingStats || isCompleted) return null;

    return (
      <div className='remaining-stats-card'>
        <div className='remaining-title'>Remaining</div>
        <div className='remaining-content'>
          <div className='remaining-stat'>
            <span className='stat-value'>{formatDistance(remainingStats.totalDistance)}</span>
            <span className='stat-label'>Distance</span>
          </div>
          <div className='remaining-stat'>
            <span className='stat-value'>{formatTime(remainingStats.totalTime)}</span>
            <span className='stat-label'>Est. Time</span>
          </div>
        </div>
      </div>
    );
  };

  const renderUpcomingItem = (item: DailyProgressItem) => (
    <div
      key={item.scheduleItemId}
      className='upcoming-item'
      style={{ borderLeftColor: getTypeColor(item.type) }}
    >
      <div className='item-header'>
        <span className='contact-name'>{item.contactName || 'No contact'}</span>
        <div className='pills'>
          {item.dumpsterSize && <span className='pill'>{item.dumpsterSize}</span>}
          {item.materialDisposing && <span className='pill'>{item.materialDisposing}</span>}
        </div>
      </div>
      <div className='item-address'>{item.address}, {item.zip}</div>
      {item.specialInstructions && (
        <div className='item-instructions'>{item.specialInstructions}</div>
      )}
    </div>
  );

  const renderUpcomingStops = () => {
    if (!nextStop || upcomingStops.length === 0) return null;

    return (
      <div className='upcoming-section'>
        <div className='upcoming-title'>Upcoming Stops</div>
        <div className='upcoming-list'>
          {upcomingStops.map(renderUpcomingItem)}
        </div>
      </div>
    );
  };

  const renderNoMoreStops = () => {
    if (!nextStop || upcomingStops.length > 0) return null;

    return (
      <div className='no-more-stops'>No more stops.</div>
    );
  };

  return (
    <div className='daily-progress-tab'>
      {getDailyProgressAPI.loading && (
        <div className='loading-state'>
          <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/>
          <span>Fetching schedule...</span>
        </div>
      )}

      {!getDailyProgressAPI.loading && (
        <div className='content'>
          <div className='progress-top'>
            <div className='stats-column'>
              {renderStatusCard()}
              {renderRemainingStats()}
              <PunchCardCard />
            </div>
            <div className='map-column'>
              <DriverLocationMap location={driverLocation} driverName={driverName} />
            </div>
          </div>
          {noSchedule && (
            <div className='empty-state'>There is no schedule for today.</div>
          )}
          {!noSchedule && (
            <>
              {renderNextStop()}
              {renderCompletedMessage()}
              {renderUpcomingStops()}
              {renderNoMoreStops()}
            </>
          )}
        </div>
      )}
    </div>
  );
}
